import React, { useState } from 'react';
import { NaphthaleneProcedure } from './NaphthaleneProcedure';
import { NaphthaleneTable } from './NaphthaleneTable';
import { NaphthaleneChart } from './NaphthaleneChart';
import { Table2, LineChart, SlidersHorizontal } from 'lucide-react';
import { useNaphthaleneStore } from '../store/useNaphthaleneStore';

export function NaphthaleneSidebar() {
    const [sidebarTab, setSidebarTab] = useState<'controls' | 'data' | 'chart'>('controls');
    
    // Derived values for the bottom summary overlay
    const currentTemperature = useNaphthaleneStore(state => state.currentTemperature);
    const labStage = useNaphthaleneStore(state => state.labStage);

    const activeClass = "interactable-btn flex-1 flex justify-center items-center gap-2 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg shadow-md transition-all";
    const inactiveClass = "interactable-btn flex-1 flex justify-center items-center gap-2 py-2.5 text-muted-foreground hover:text-foreground hover:bg-muted font-medium transition-all cursor-pointer";

    return (
        <div className="w-full lg:w-[400px] xl:w-[450px] shrink-0 lg:h-full bg-card/95 backdrop-blur-xl border-t lg:border-t-0 lg:border-l border-indigo-500/10 flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)] z-20">
            {/* Tab Navigation */}
            <div className="p-4 border-b border-indigo-500/10">
                <div className="grid grid-cols-3 bg-muted/40 p-1 rounded-xl border border-border shadow-inner gap-1">
                    <div
                        className={sidebarTab === 'controls' ? activeClass : inactiveClass}
                        onClick={() => setSidebarTab('controls')}
                    >
                        <SlidersHorizontal className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">Controls</span>
                    </div>
                    <div
                        className={sidebarTab === 'data' ? activeClass : inactiveClass}
                        onClick={() => setSidebarTab('data')}
                    >
                        <Table2 className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">Data</span>
                    </div>
                    <div
                        className={sidebarTab === 'chart' ? activeClass : inactiveClass}
                        onClick={() => setSidebarTab('chart')}
                    >
                        <LineChart className="w-4 h-4" />
                        <span className="text-xs sm:text-sm">Chart</span>
                    </div>
                </div>
            </div>

            {/* Tab Content Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 customized-scrollbar flex flex-col">
                {sidebarTab === 'controls' && (
                    <div className="flex-1 flex flex-col pt-2">
                        <NaphthaleneProcedure />

                        {/* Optional Bottom summary overlay mimicking titration's pH summary */}
                        <div className="mt-auto pt-6">
                            <div className="flex justify-between items-center bg-background/50 p-4 rounded-2xl border border-border/50 shadow-inner">
                                <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Temperature</span>
                                    <div className="text-3xl font-mono font-bold text-red-500 tracking-tight" style={{ textShadow: '0 0 15px rgba(239,68,68,0.3)' }}>
                                        {currentTemperature.toFixed(1)}°C
                                    </div>
                                </div>
                                <div className="w-[1px] h-12 bg-border/50 mx-4" />
                                <div className="flex flex-col text-right">
                                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Stage</span>
                                    <div className="text-lg font-semibold text-blue-500 tracking-tight capitalize" style={{ textShadow: '0 0 15px rgba(59,130,246,0.3)' }}>
                                        {labStage}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {sidebarTab === 'data' && (
                    <div className="flex-1 flex flex-col h-full"> 
                        <NaphthaleneTable />
                    </div>
                )}
                {sidebarTab === 'chart' && (
                    <div className="flex-1 flex flex-col h-full min-h-[400px]">
                        <NaphthaleneChart />
                    </div>
                )}
            </div>
        </div>
    );
}
